import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import type { MetricWithObject } from "types/BusinessDashboard";
import type { MonthlyParams } from "api/businessDashboard";
import {
  SALES_PASS_TYPE_MAP,
  salesDashboardTabOptions,
} from "constants/businessDashboard";
import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";
import {
  useSalesSummaryQuery,
  useTargetDataQuery,
} from "queries/Business/Dashboard/useDashboardQueries";

import { SalesStatCard } from "./sales-stat-card";
import { SalesDashboardSummarySkeleton } from "./sales-dashboard-summary.skeleton";
import { SalesDashboardSummaryError } from "./sales-dashboard-summary.error";

dayjs.locale("ko");

const Wrapper = styled.div`
  padding: 0 16px;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;

export const SalesDashboardSummary = () => {
  const { searchParams } = useUpdateUrlParams();
  const activeTab = searchParams.get("tab") || salesDashboardTabOptions[0].id;

  const referenceMonth = searchParams.get("month") || dayjs().format("YYYY-MM");

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const apiParams = useMemo(() => {
    const params: MonthlyParams = {
      referenceMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [referenceMonth, partnerInfo]);
  const targetApiParams = useMemo(() => {
    const params: MonthlyParams = {
      referenceMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [referenceMonth, partnerInfo]);

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    refetch: refetchDetailData,
  } = useSalesSummaryQuery(apiParams);
  const { refetch: refetchTargetData } = useTargetDataQuery(targetApiParams, {
    enabled: false,
  });

  const handleRefresh = () => {
    refetchDetailData();
    refetchTargetData();
  };

  if (isSummaryLoading) return <SalesDashboardSummarySkeleton />;
  if (isSummaryError || !summaryData)
    return <SalesDashboardSummaryError onRetry={handleRefresh} />;

  const { stats } = (() => {
    const statsResult: { [key: string]: MetricWithObject } = {
      total: summaryData.data.total,
      pt: summaryData.data.personalLesson,
      groupClass: summaryData.data.groupClass,
      membership: summaryData.data.membership,
      option: summaryData.data.option,
      etc: summaryData.data.etc,
    };

    return {
      stats: statsResult,
    };
  })();

  return (
    <Wrapper>
      {activeTab === "total" &&
        Object.entries(SALES_PASS_TYPE_MAP)
          .slice(0, 1)
          .map(([key]) => (
            <SalesStatCard
              key={key}
              title="총 매출"
              keyName="monthlySales"
              metric={stats[key]}
              big
            />
          ))}
      {activeTab === "pt" &&
        Object.entries(SALES_PASS_TYPE_MAP)
          .slice(1, 2)
          .map(([key]) => (
            <SalesStatCard key={key} title="개인레슨" metric={stats[key]} big />
          ))}
      {activeTab === "groupClass" &&
        Object.entries(SALES_PASS_TYPE_MAP)
          .slice(2, 3)
          .map(([key]) => (
            <SalesStatCard key={key} title="그룹수업" metric={stats[key]} big />
          ))}
      {activeTab === "membership" &&
        Object.entries(SALES_PASS_TYPE_MAP)
          .slice(3, 4)
          .map(([key]) => (
            <SalesStatCard key={key} title="회원권" metric={stats[key]} big />
          ))}
      {activeTab === "option" &&
        Object.entries(SALES_PASS_TYPE_MAP)
          .slice(4, 5)
          .map(([key]) => (
            <SalesStatCard key={key} title="옵션이용" metric={stats[key]} big />
          ))}
      {activeTab === "etc" &&
        Object.entries(SALES_PASS_TYPE_MAP)
          .slice(5, 6)
          .map(([key]) => (
            <SalesStatCard key={key} title="기타" metric={stats[key]} big />
          ))}
    </Wrapper>
  );
};
